import {useEffect, useState} from "react"
import {BasePopup} from "components/popup"
import {AddItemCircleButton, BaseButton, SecondaryButton} from "components/buttons"
import {
    BreakTimeFieldErrors,
    BreakTimeForm,
    cloneBreakTimes,
    createDefaultBreakTime,
    getBreakTimeFieldErrors,
    hasBreakTimeFieldErrors
} from "types/work-schedule"
import {BreakTimeItem} from "sections/CalendarSettings/parts/WorkScheduleDetails/parts/WorkDayItem/BreakTimeItem"
import {toastError} from "helpers"
import styles from "./ShiftBreakTimesPopup.module.css"

type Props = {
    isVisible: boolean
    initialBreakTimes: BreakTimeForm[]
    loading?: boolean
    onClose: () => void
    onApply: (breakTimes: BreakTimeForm[]) => void
}

export function ShiftBreakTimesPopup(props: Props) {
    const {isVisible, initialBreakTimes, loading, onClose, onApply} = props
    const [breakTimes, setBreakTimes] = useState<BreakTimeForm[]>([])
    const [fieldErrorsByIndex, setFieldErrorsByIndex] = useState<Record<number, BreakTimeFieldErrors>>({})

    useEffect(() => {
        if (isVisible) {
            setBreakTimes(cloneBreakTimes(initialBreakTimes))
            setFieldErrorsByIndex({})
        }
    }, [isVisible, initialBreakTimes])

    const onChangeBreakTime = (index: number, breakTime: BreakTimeForm) => {
        const next = [...breakTimes]
        next[index] = breakTime
        setBreakTimes(next)
        if (fieldErrorsByIndex[index]) {
            const nextErrors = {...fieldErrorsByIndex}
            delete nextErrors[index]
            setFieldErrorsByIndex(nextErrors)
        }
    }

    const onAddBreakTime = () => {
        setBreakTimes([...breakTimes, createDefaultBreakTime()])
    }

    const onRemoveBreakTime = (index: number) => {
        setBreakTimes(breakTimes.filter((_, i) => i !== index))
        setFieldErrorsByIndex({})
    }

    const handleApply = () => {
        const nextErrors: Record<number, BreakTimeFieldErrors> = {}
        breakTimes.forEach((bt, index) => {
            const fieldErrors = getBreakTimeFieldErrors(bt)
            if (hasBreakTimeFieldErrors(fieldErrors)) {
                nextErrors[index] = fieldErrors
            }
        })

        if (Object.keys(nextErrors).length > 0) {
            setFieldErrorsByIndex(nextErrors)
            toastError("Please fill required break time fields (From, To, Max Minutes)")
            return
        }

        setFieldErrorsByIndex({})
        onApply(cloneBreakTimes(breakTimes))
    }

    return (
        <BasePopup isShow={isVisible} onClose={onClose} width="520px" leftIcon="TIME_CLOCK_OUTLINE">
            <div className={styles.content}>
                <div className={styles.title}>Break Times for All Days</div>
                <div className={styles.description}>
                    Configure break times once — they will be applied to every day in this shift (Sunday through
                    Saturday).
                </div>

                <div className={styles.breakTimesList}>
                    {breakTimes.map((breakTime, index) => (
                        <BreakTimeItem
                            key={index}
                            item={breakTime}
                            fieldErrors={fieldErrorsByIndex[index]}
                            onChange={(updated) => onChangeBreakTime(index, updated)}
                            onRemove={() => onRemoveBreakTime(index)}
                        />
                    ))}
                    <AddItemCircleButton title="Add Break Time" onClick={onAddBreakTime} />
                </div>

                <div className={styles.footer}>
                    <SecondaryButton title="Cancel" onClick={onClose} disabled={loading} />
                    <BaseButton title="Apply & Save" onClick={handleApply} loading={loading} />
                </div>
            </div>
        </BasePopup>
    )
}
